
import React, { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { mockFluxoCaixaData } from "../resumo/mockData";
import FluxoCaixaSection from "../resumo/FluxoCaixaSection";
import SaldoSection from "../resumo/SaldoSection";
import RecebimentosSection from "../resumo/RecebimentosSection";
import PagamentosSection from "../resumo/PagamentosSection";
import { ContaBancaria } from "@/types/financeiro";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const ResumoTabContent: React.FC = () => {
  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [periodoSelecionado, setPeriodoSelecionado] = useState<Date>(new Date());
  const [fluxoCaixaData, setFluxoCaixaData] = useState(mockFluxoCaixaData);
  const [isLoading, setIsLoading] = useState(false);

  // Load saved accounts from localStorage on component mount
  useEffect(() => {
    const savedContas = localStorage.getItem('contas_bancarias');
    if (savedContas) {
      setContas(JSON.parse(savedContas));
    }
  }, []);

  // Function to handle adding a new account
  const adicionarConta = () => {
    // Navigate to the contas tab and trigger the new account form
    const event = new CustomEvent('change-tab', { 
      detail: { tab: 'contas', action: 'nova-conta' } 
    });
    window.dispatchEvent(event);
  };

  // Generate random data when changing periods (for demo purposes)
  // In a real application, this would be replaced with actual API calls
  const generateRandomData = () => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const newData = mockFluxoCaixaData.map(item => ({
        ...item,
        receitas: Math.random() * 5000,
        despesas: Math.random() * 3000
      }));
      setFluxoCaixaData(newData);
      setIsLoading(false);
      
      // Notify user
      toast({
        title: "Dados atualizados",
        description: "Os dados financeiros foram atualizados para o período selecionado.",
      });
    }, 800);
  };

  useEffect(() => {
    // This would be an API call in a real application
    // For demonstration, we'll simulate a loading state
    setIsLoading(true);
    
    // Simulate API call to load data for the selected period
    const timer = setTimeout(() => {
      generateRandomData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [periodoSelecionado]);

  const handlePrint = () => {
    window.print();
    toast({
      title: "Impressão",
      description: "Enviando para impressão...",
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="space-y-6">
      {/* Header com título e botões */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-500"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline text-xs">Voltar</span>
          </Button>
          <h1 className="text-xl text-gray-700">Resumo</h1>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handlePrint} 
          className="flex items-center gap-1 text-gray-500"
        >
          <Printer className="h-4 w-4" />
          <span className="hidden sm:inline text-xs">Imprimir</span>
        </Button>
      </div>
      
      {/* Layout principal com proporção 30/70 */}
      <div className="grid grid-cols-12 gap-6">
        {/* Coluna da esquerda - Saldo (30%) */}
        <div className="col-span-12 lg:col-span-4 financeiro-card bg-white rounded-lg shadow-sm h-[520px]"> 
          <SaldoSection 
            contas={contas}
            setContas={setContas}
            adicionarConta={adicionarConta}
          />
        </div>
        
        {/* Coluna da direita - Fluxo de Caixa (70%) */}
        <div className="col-span-12 lg:col-span-8 financeiro-card bg-white rounded-lg shadow-sm h-[520px]"> 
          <FluxoCaixaSection 
            periodoSelecionado={periodoSelecionado}
            setPeriodoSelecionado={setPeriodoSelecionado}
            fluxoCaixaData={fluxoCaixaData}
            generateRandomData={generateRandomData}
          />
        </div>
      </div>
      
      {/* Seções de Recebimentos e Pagamentos em nova grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-gray-50 p-5 rounded-lg">
        <div className="financeiro-card bg-white rounded-lg shadow-sm overflow-hidden h-[460px]"> 
          <RecebimentosSection />
        </div>
        
        <div className="financeiro-card bg-white rounded-lg shadow-sm overflow-hidden h-[460px]"> 
          <PagamentosSection />
        </div>
      </div>
    </div>
  );
};

export default ResumoTabContent;
